import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export function usePostLikes(postId: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['post-likes', postId, user?.id],
    queryFn: async () => {
      // Get total likes count
      const { count, error: countError } = await supabase
        .from('post_likes')
        .select('*', { count: 'exact', head: true })
        .eq('post_id', postId);

      if (countError) throw countError;

      // Check if current user has liked
      let hasLiked = false;
      if (user?.id) {
        const { data: userLike, error: userLikeError } = await supabase
          .from('post_likes')
          .select('id')
          .eq('post_id', postId)
          .eq('user_id', user.id)
          .maybeSingle();

        if (userLikeError) throw userLikeError;
        hasLiked = !!userLike;
      }

      return {
        count: count || 0,
        hasLiked,
      };
    },
    enabled: !!postId,
  });
}

export function useLikePost() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (postId: string) => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('post_likes')
        .insert({
          post_id: postId,
          user_id: user.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, postId) => {
      queryClient.invalidateQueries({ queryKey: ['post-likes', postId] });
      queryClient.invalidateQueries({ queryKey: ['posts'] });
    },
  });
}

export function useUnlikePost() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (postId: string) => {
      if (!user?.id) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('post_likes')
        .delete()
        .eq('post_id', postId)
        .eq('user_id', user.id);

      if (error) throw error;
    },
    onSuccess: (_, postId) => {
      queryClient.invalidateQueries({ queryKey: ['post-likes', postId] });
      queryClient.invalidateQueries({ queryKey: ['posts'] });
    },
  });
}

export function useToggleLike() {
  const likePost = useLikePost();
  const unlikePost = useUnlikePost();

  return {
    toggleLike: async (postId: string, currentlyLiked: boolean) => {
      if (currentlyLiked) {
        await unlikePost.mutateAsync(postId);
      } else {
        await likePost.mutateAsync(postId);
      }
    },
    isPending: likePost.isPending || unlikePost.isPending,
  };
}

// Comment Likes
export function useLikeComment() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ commentId, postId }: { commentId: string; postId: string }) => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('comment_likes')
        .insert({
          comment_id: commentId,
          user_id: user.id,
        })
        .select()
        .single();

      if (error) throw error;
      return { data, postId };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['comments', result.postId] });
    },
  });
}

export function useUnlikeComment() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ commentId, postId }: { commentId: string; postId: string }) => {
      if (!user?.id) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('comment_likes')
        .delete()
        .eq('comment_id', commentId)
        .eq('user_id', user.id);

      if (error) throw error;
      return { postId };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['comments', result.postId] });
    },
  });
}

export function useToggleCommentLike() {
  const likeComment = useLikeComment();
  const unlikeComment = useUnlikeComment();

  return {
    toggleLike: async (commentId: string, postId: string, currentlyLiked: boolean) => {
      if (currentlyLiked) {
        await unlikeComment.mutateAsync({ commentId, postId });
      } else {
        await likeComment.mutateAsync({ commentId, postId });
      }
    },
    isPending: likeComment.isPending || unlikeComment.isPending,
  };
}
